import React from "react";
import toast from "react-hot-toast";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  Upload, FileSpreadsheet, Download, X, Plus, ChevronRight, AlertTriangle, CheckCircle, Zap,
} from "lucide-react";
import { Drawer } from "./Drawer";
import { Card, Button } from "./primitives";
import { Pagination } from "./DataTable";
import { Field, Input, Modal } from "./forms";
import { AntDropdown, AntMultiSelect } from "../../../components/UI";
import { isCrmEnabledForOrg } from "../../../modules/crm/utils/orgGating";
import { getAllStructurePositionNames, resolvePositionInput } from "../../../utils/teamPositionMatch";
import { resolveRoleInput, roleImportHint } from "../../../utils/bulkImportNormalize";
import {
  collectDuplicateImportErrors,
  downloadBulkImportErrorReport,
  groupBulkImportErrors,
  mergeBulkImportErrors,
} from "../../../utils/bulkImportErrors";

const DEFAULT_PERMISSIONS = {
  addProject: false,
  addTeamMember: false,
  viewReports: true,
  viewActivity: true,
  editProfile: true,
  crmAccess: false,
};

function blankBulkUser(id = Date.now()) {
  return {
    id,
    firstName: "",
    lastName: "",
    email: "",
    role: "org_user",
    phone: "",
    department: "",
    position: "",
    password: "",
    reportingManagers: [],
    permissions: { ...DEFAULT_PERMISSIONS },
    expanded: false,
  };
}

function getAccessiblePositions(structure, currentUser) {
  const all = [];
  (structure || []).forEach((lvl) => (lvl.positions || []).forEach((p) => all.push({ value: p.roleName, label: p.roleName })));
  const role = currentUser?.role;
  if (!structure?.length || role === "main_admin" || role === "org_admin" || !currentUser?.position) return all;
  let myLevel = -1;
  for (let i = 0; i < structure.length; i++) {
    if ((structure[i].positions || []).some((p) => (p.roleName || "").trim() === (currentUser.position || "").trim())) { myLevel = i; break; }
  }
  if (myLevel === -1) return [];
  const lower = [];
  for (let i = myLevel + 1; i < structure.length; i++) (structure[i].positions || []).forEach((p) => lower.push({ value: p.roleName, label: p.roleName }));
  return lower;
}

function isMongoId(value) {
  return /^[a-f\d]{24}$/i.test(String(value || ""));
}

// Match CSV/import value to an existing team member by email (preferred) or name.
// If no match, keep the original value so the admin can wire it up later.
function resolveReportingManagerInput(input, allUsers = []) {
  const raw = String(input || "").trim();
  if (!raw) return raw;
  if (isMongoId(raw)) return raw;

  const normalized = raw.toLowerCase();
  if (normalized.includes("@")) {
    const byEmail = allUsers.find((u) => u.email?.toLowerCase().trim() === normalized);
    if (byEmail) return byEmail._id || byEmail.id;
    return raw;
  }

  const byFullName = allUsers.find((u) => `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase().trim() === normalized);
  if (byFullName) return byFullName._id || byFullName.id;

  const partial = allUsers.find((u) => {
    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase().trim();
    return fullName.includes(normalized) || normalized.includes(fullName);
  });
  if (partial) return partial._id || partial.id;

  return raw;
}

function resolveReportingManagerIdsForSubmit(entries, allUsers = []) {
  return (entries || [])
    .map((entry) => resolveReportingManagerInput(entry, allUsers))
    .filter((entry) => isMongoId(entry));
}

function parseReportingManagersFromInput(text, allUsers = []) {
  return String(text || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => resolveReportingManagerInput(s, allUsers));
}

function formatReportingManagersForInput(managers, allUsers = []) {
  return (managers || []).map((m) => {
    if (isMongoId(m)) {
      const u = allUsers.find((x) => String(x._id || x.id) === String(m));
      return u?.email || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || String(m);
    }
    return String(m);
  }).join(", ");
}

function getAllStructurePositions(structure) {
  return getAllStructurePositionNames(structure).map((name) => ({ value: name, label: name }));
}

function getPositionOptionsForRow(position, baseOptions) {
  if (!position) return baseOptions;
  if (baseOptions.some((o) => o.value === position)) return baseOptions;
  return [{ value: position, label: position }, ...baseOptions];
}

function tableFieldStyle(t, minWidth) {
  return {
    height: 36,
    minHeight: 36,
    width: "100%",
    minWidth,
    padding: "6px 10px",
    fontSize: t.font.size.sm,
    fontFamily: t.font.family,
    boxSizing: "border-box",
    borderRadius: t.r.sm,
    border: `1px solid ${t.c.border}`,
    background: t.c.surface,
    color: t.c.text,
    outline: "none",
  };
}

const DROPDOWN_FULL_WIDTH = { width: "100%" };

function BulkImportTableDropdown({ value, onChange, options, placeholder, allowClear = true, dropdownPopup }) {
  return (
    <AntDropdown
      value={value || undefined}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      allowClear={allowClear}
      showSearch
      size="small"
      style={DROPDOWN_FULL_WIDTH}
      popupMatchSelectWidth={false}
      {...dropdownPopup}
    />
  );
}

function BulkImportApplyDropdown({ value, onChange, options, placeholder, allowClear = true, dropdownPopup }) {
  return (
    <AntDropdown
      value={value || undefined}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      allowClear={allowClear}
      showSearch
      style={DROPDOWN_FULL_WIDTH}
      popupMatchSelectWidth={false}
      {...dropdownPopup}
    />
  );
}

function BulkImportApplyMultiSelect({ value, onChange, options, placeholder, dropdownPopup }) {
  return (
    <AntMultiSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      showSearch
      allowClear
      maxTagCount="responsive"
      style={DROPDOWN_FULL_WIDTH}
      popupMatchSelectWidth={false}
      {...dropdownPopup}
    />
  );
}

const BulkImportRoleDropdown = React.memo(function BulkImportRoleDropdown({ value, onChange, options, dropdownPopup }) {
  return (
    <BulkImportTableDropdown
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Select role"
      allowClear={false}
      dropdownPopup={dropdownPopup}
    />
  );
});

const BulkImportPositionDropdown = React.memo(function BulkImportPositionDropdown({ value, onChange, position, baseOptions, dropdownPopup }) {
  const options = React.useMemo(
    () => getPositionOptionsForRow(position, baseOptions),
    [position, baseOptions],
  );
  return (
    <BulkImportTableDropdown
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Select position"
      allowClear
      dropdownPopup={dropdownPopup}
    />
  );
});

const BulkImportRmInput = React.memo(function BulkImportRmInput({ managers, allUsers, onCommit, style }) {
  const [text, setText] = React.useState(() => formatReportingManagersForInput(managers, allUsers));
  const managersKey = React.useMemo(() => JSON.stringify(managers || []), [managers]);

  React.useEffect(() => {
    setText(formatReportingManagersForInput(managers, allUsers));
  }, [managersKey, allUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onCommit(parseReportingManagersFromInput(text, allUsers))}
      placeholder="email@company.com"
      style={style}
    />
  );
});

function BulkImportResultModal({ t, open, onClose, successCount = 0, failures = [] }) {
  const groups = React.useMemo(() => groupBulkImportErrors(failures), [failures]);
  const previewLimit = 8;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import results"
      subtitle={`${successCount} added · ${failures.length} failed`}
      icon={<AlertTriangle size={18} />}
      width={720}
      footer={(
        <>
          {failures.length > 0 && (
            <Button variant="secondary" leftIcon={<Download size={14} />} onClick={() => downloadBulkImportErrorReport(failures)}>
              Download error report
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>Close</Button>
        </>
      )}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
        {successCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: t.r.sm, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.18)", color: t.c.text }}>
            <CheckCircle size={16} style={{ color: "#16a34a", flexShrink: 0 }} />
            <span style={{ fontSize: t.font.size.sm }}>{successCount} member{successCount === 1 ? "" : "s"} imported successfully.</span>
          </div>
        )}

        {failures.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: t.s[3] }}>
            <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.5 }}>
              Fix the rows below and re-import, or download the full error report for all {failures.length} failed email{failures.length === 1 ? "" : "s"}.
            </div>
            {groups.map((group) => (
              <Card key={group.error} padding={14}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: t.font.size.sm, fontWeight: 600, color: t.c.text }}>{group.error}</div>
                  <span style={{ fontSize: t.font.size.xs, fontWeight: 600, color: t.c.textSecondary, whiteSpace: "nowrap" }}>{group.count} row{group.count === 1 ? "" : "s"}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                  {group.items.slice(0, previewLimit).map((item) => (
                    <div key={`${group.error}-${item.email}-${item.row || ""}`} style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>
                      {item.row ? `Row ${item.row}: ` : ""}{item.email}
                    </div>
                  ))}
                  {group.count > previewLimit && (
                    <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, paddingTop: 4 }}>
                      …and {group.count - previewLimit} more with the same issue
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// CSV/XLSX bulk member import — full parity with legacy Team.js bulk flow.
export function BulkImportDrawer({
  t,
  api,
  user,
  open,
  onClose,
  onDone,
  structure = [],
  allUsers = [],
  roles = [],
  onAddPosition,
}) {
  const [bulkUsers, setBulkUsers] = React.useState([blankBulkUser(1)]);
  const [fileName, setFileName] = React.useState("");
  const [parseError, setParseError] = React.useState("");
  const [uploadSuccess, setUploadSuccess] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [commonReportingManagers, setCommonReportingManagers] = React.useState([]);
  const [commonPermissions, setCommonPermissions] = React.useState({ ...DEFAULT_PERMISSIONS });
  const [commonPosition, setCommonPosition] = React.useState("");
  const [commonRole, setCommonRole] = React.useState("org_user");
  const [commonPassword, setCommonPassword] = React.useState("");
  const [importResult, setImportResult] = React.useState(null);
  const fileRef = React.useRef(null);

  const accessiblePositions = React.useMemo(() => getAccessiblePositions(structure, user), [structure, user]);
  const allStructurePositions = React.useMemo(() => getAllStructurePositions(structure), [structure]);
  const basePositionOptions = React.useMemo(
    () => (accessiblePositions.length > 0 ? accessiblePositions : allStructurePositions),
    [accessiblePositions, allStructurePositions],
  );
  const commonPositionOptions = React.useMemo(
    () => getPositionOptionsForRow(commonPosition, basePositionOptions),
    [commonPosition, basePositionOptions],
  );
  const showCrmPermission = isCrmEnabledForOrg(window.__orgProfile) && user?.role !== "org_user";
  const roleOptions = roles.length ? roles : [{ value: "org_user", label: "Member" }, { value: "org_admin", label: "Admin" }];
  const roleDropdownOptions = React.useMemo(
    () => roleOptions.map((r) => ({ value: r.value, label: r.label || r.value })),
    [roleOptions],
  );
  const commonManagerOptions = React.useMemo(
    () => allUsers
      .filter((u) => u.role === "org_admin" || u.position)
      .map((m) => ({
        value: m._id || m.id,
        label: `${m.firstName || ""} ${m.lastName || ""}`.trim() + (m.email ? ` (${m.email})` : "") + (m.position ? ` · ${m.position}` : ""),
      })),
    [allUsers],
  );
  const nameInputStyle = React.useMemo(() => tableFieldStyle(t, 120), [t]);
  const emailInputStyle = React.useMemo(() => tableFieldStyle(t, 220), [t]);
  const phoneInputStyle = React.useMemo(() => tableFieldStyle(t, 150), [t]);
  const textInputStyle = React.useMemo(() => tableFieldStyle(t, 120), [t]);
  const rmInputStyle = React.useMemo(() => ({ ...tableFieldStyle(t, 220), minWidth: 220 }), [t]);
  const dropdownPopup = React.useMemo(() => ({
    getPopupContainer: () => document.body,
    styles: { popup: { root: { zIndex: 10050 } } },
  }), []);

  React.useEffect(() => {
    if (!open) {
      setBulkUsers([blankBulkUser(1)]);
      setFileName("");
      setParseError("");
      setUploadSuccess(false);
      setCommonReportingManagers([]);
      setCommonPermissions({ ...DEFAULT_PERMISSIONS });
      setCommonPosition("");
      setCommonRole("org_user");
      setCommonPassword("");
      setImportResult(null);
      setPage(1);
    }
  }, [open]);

  const findColumn = (actualColumns, possibleNames) => {
    for (const actualCol of actualColumns) {
      const normalizedActual = actualCol.toLowerCase().trim();
      for (const possibleName of possibleNames) {
        const normalizedPossible = possibleName.toLowerCase().trim();
        if (normalizedActual === normalizedPossible || normalizedActual.includes(normalizedPossible) || normalizedPossible.includes(normalizedActual)) return actualCol;
      }
    }
    return null;
  };

  const validateAndMapCsv = (data) => {
    const actualColumns = Object.keys(data[0] || {}).map((c) => c.trim());
    const required = ["first name", "last name", "email"];
    const missing = required.filter((req) => !actualColumns.some((c) => {
      const a = c.toLowerCase(); return a === req || a.includes(req) || req.includes(a);
    }));
    if (missing.length) throw new Error(`Missing required columns: ${missing.join(", ")}. Found: ${actualColumns.join(", ")}`);

    const mappedUsers = data.map((row, index) => {
      const firstName = row[findColumn(actualColumns, ["First Name", "FirstName", "first_name", "First Name*"])] || "";
      const lastName = row[findColumn(actualColumns, ["Last Name", "LastName", "last_name", "Last Name*"])] || "";
      const email = row[findColumn(actualColumns, ["Email", "email", "Email*", "Email Address"])] || "";
      const role = row[findColumn(actualColumns, ["Role", "role", "User Role"])] || "org_user";
      const position = row[findColumn(actualColumns, ["Position", "position", "Job Position"])] || "";
      const phone = row[findColumn(actualColumns, ["Phone", "phone", "Phone Number", "Mobile"])] || "";
      const password = row[findColumn(actualColumns, ["Password", "password", "Pass", "User Password"])] || "";
      const reportingManagerStr = row[findColumn(actualColumns, ["Reporting Manager", "Reporting Managers", "Manager", "reporting_manager"])] || "";
      const reportingManagers = reportingManagerStr ? String(reportingManagerStr).split(",").map((m) => m.trim()).filter(Boolean) : [];
      return {
        id: Date.now() + index,
        firstName: String(firstName).trim(),
        lastName: String(lastName).trim(),
        email: String(email).trim().toLowerCase(),
        role: resolveRoleInput(String(role).trim(), roleOptions).value,
        phone: String(phone).trim(),
        department: "",
        position: String(position).trim(),
        password: String(password).trim(),
        reportingManagers,
        permissions: { ...DEFAULT_PERMISSIONS },
        expanded: false,
      };
    }).filter((u) => u.firstName || u.lastName || u.email);

    if (!mappedUsers.length) throw new Error("No valid data found in the file.");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidRows = [];
    mappedUsers.forEach((u, index) => {
      const rowNumber = index + 2;
      if (!u.firstName || !u.lastName || !u.email) invalidRows.push(`Row ${rowNumber}: Missing required fields (First Name, Last Name, or Email)`);
      else if (!emailRegex.test(u.email)) invalidRows.push(`Row ${rowNumber}: Invalid email format - ${u.email}`);
    });
    if (invalidRows.length) {
      throw new Error(`Validation errors found:\n${invalidRows.slice(0, 15).join("\n")}${invalidRows.length > 15 ? `\n... and ${invalidRows.length - 15} more errors` : ""}`);
    }

    const structurePositionLabels = getAllStructurePositionNames(structure);

    const dataValidationErrors = [];
    const clearedPositions = [];
    let matchedManagerCount = 0;
    let pendingManagerCount = 0;

    mappedUsers.forEach((u, index) => {
      const rowNumber = index + 2;
      if (u.position?.trim()) {
        const resolved = resolvePositionInput(u.position, structure);
        if (resolved.matched) u.position = resolved.value;
        else {
          clearedPositions.push({ rowNumber, email: u.email, value: u.position });
          u.position = "";
        }
      }
      if (u.reportingManagers?.length) {
        u.reportingManagers = u.reportingManagers.map((managerInput) => {
          const resolved = resolveReportingManagerInput(managerInput, allUsers);
          if (isMongoId(resolved)) matchedManagerCount += 1;
          else pendingManagerCount += 1;
          return resolved;
        });
      }
      const resolvedRole = resolveRoleInput(u.role, roleOptions);
      if (resolvedRole.matched) u.role = resolvedRole.value;
      else if (u.role) {
        dataValidationErrors.push(`Row ${rowNumber} (${u.email}): Invalid Role "${u.role}". Valid roles: ${roleImportHint(roleOptions)}`);
      } else {
        u.role = "org_user";
      }
    });

    if (clearedPositions.length) {
      const sample = clearedPositions.slice(0, 3).map((c) => `"${c.value}"`).join(", ");
      toast(
        `${clearedPositions.length} row(s) had an unknown Position (${sample}${clearedPositions.length > 3 ? "…" : ""}) — cleared. Available: ${structurePositionLabels.slice(0, 6).join(", ")}${structurePositionLabels.length > 6 ? "…" : ""}.`,
        { icon: "⚠️", duration: 8000, style: { maxWidth: "520px", whiteSpace: "pre-line" } },
      );
    }
    if (matchedManagerCount > 0 || pendingManagerCount > 0) {
      const parts = [];
      if (matchedManagerCount > 0) parts.push(`${matchedManagerCount} reporting manager reference(s) matched existing team members`);
      if (pendingManagerCount > 0) parts.push(`${pendingManagerCount} kept as entered (no matching team member yet)`);
      toast(parts.join(". ") + ".", { icon: matchedManagerCount > 0 ? "✅" : "ℹ️", duration: 6000 });
    }

    if (dataValidationErrors.length) {
      throw new Error(`Data validation errors found:\n${dataValidationErrors.slice(0, 20).join("\n")}${dataValidationErrors.length > 20 ? `\n... and ${dataValidationErrors.length - 20} more errors` : ""}`);
    }

    return mappedUsers;
  };

  const onFile = (file) => {
    if (!file) return;
    setParseError("");
    setUploadSuccess(false);
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) { setParseError("Please upload a .csv, .xlsx or .xls file"); return; }
    setFileName(file.name);
    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let data = [];
        if (ext === "csv") {
          const res = Papa.parse(e.target.result, { header: true, skipEmptyLines: true, transformHeader: (h) => h.trim() });
          if (res.errors.length) throw new Error(`CSV parsing errors: ${res.errors.slice(0, 5).map((err) => `Row ${err.row + 1}: ${err.message}`).join(", ")}`);
          data = res.data;
        } else {
          const wb = XLSX.read(e.target.result, { type: "binary" });
          data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "", raw: false });
        }
        if (!data.length) throw new Error("File is empty or contains no data");
        const mapped = validateAndMapCsv(data);
        setBulkUsers(mapped);
        setPage(1);
        setUploadSuccess(true);
        toast.success(`Successfully imported ${mapped.length} user(s) from file!`);
      } catch (err) {
        setParseError(err.message || "Could not parse file");
        setBulkUsers([blankBulkUser(1)]);
      } finally { setIsUploading(false); }
    };
    reader.onerror = () => { setParseError("Error reading file. Please try again."); setIsUploading(false); };
    if (ext === "csv") reader.readAsText(file); else reader.readAsBinaryString(file);
  };

  const downloadTemplate = () => {
    const firstPosition = accessiblePositions[0]?.value || "Developer";
    const secondPosition = accessiblePositions[1]?.value || accessiblePositions[0]?.value || "Developer";
    const exampleManager = allUsers.filter((u) => u.role === "org_admin" || u.position)[0];
    const managerLabel = exampleManager ? `${exampleManager.firstName} ${exampleManager.lastName}` : "";
    const csv = [
      ["First Name", "Last Name", "Email", "Role", "Position", "Phone", "Reporting Manager", "Password"],
      ["John", "Doe", "john.doe@example.com", "org_user", firstPosition, "+1234567890", managerLabel, ""],
      ["Jane", "Smith", "jane.smith@example.com", "org_user", secondPosition, "+1234567891", "", ""],
    ].map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "team_members_template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Template downloaded!");
  };

  const updateBulkUser = (id, field, value) => setBulkUsers((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const updateBulkUserReportingManagers = (id, managerIds) => setBulkUsers((rows) => rows.map((r) => (r.id === id ? { ...r, reportingManagers: managerIds } : r)));
  const updateBulkUserPermission = (id, permission, value) => setBulkUsers((rows) => rows.map((r) => (r.id === id ? { ...r, permissions: { ...r.permissions, [permission]: value } } : r)));
  const toggleExpanded = (id) => setBulkUsers((rows) => rows.map((r) => (r.id === id ? { ...r, expanded: !r.expanded } : r)));
  const addRow = () => setBulkUsers((rows) => [...rows, blankBulkUser()]);
  const removeRow = (id) => {
    if (bulkUsers.length <= 1) { toast.error("At least one row is required"); return; }
    setBulkUsers((rows) => rows.filter((r) => r.id !== id));
  };

  const applyReportingManagersToAll = () => {
    if (!commonReportingManagers.length) { toast.error("Please select at least one reporting manager first"); return; }
    const managersToApply = [...commonReportingManagers];
    setBulkUsers((rows) => rows.map((r) => ({ ...r, reportingManagers: managersToApply })));
    toast.success(`Applied ${managersToApply.length} reporting manager(s) to all ${bulkUsers.length} row(s)`);
  };
  const applyPermissionsToAll = () => {
    setBulkUsers((rows) => rows.map((r) => ({ ...r, permissions: { ...commonPermissions } })));
    toast.success(`Applied permissions to all ${bulkUsers.length} row(s)`);
  };
  const applyPositionToAll = () => {
    if (!commonPosition) { toast.error("Please select a position first"); return; }
    setBulkUsers((rows) => rows.map((r) => ({ ...r, position: commonPosition })));
    toast.success(`Applied position "${commonPosition}" to all ${bulkUsers.length} row(s)`);
  };
  const applyRoleToAll = (role) => {
    setBulkUsers((rows) => rows.map((r) => ({ ...r, role, permissions: role === "org_admin" ? undefined : (r.permissions || { ...DEFAULT_PERMISSIONS }) })));
    toast.success(`Applied role "${role}" to all ${bulkUsers.length} row(s)`);
  };
  const applyDepartmentToAll = (department) => {
    setBulkUsers((rows) => rows.map((r) => ({ ...r, department: department || "" })));
    toast.success(`Applied department "${department || "(empty)"}" to all ${bulkUsers.length} row(s)`);
  };
  const applyPasswordToAll = () => {
    const pwd = (commonPassword || "").trim();
    if (!pwd) { toast.error("Enter a common password first"); return; }
    setBulkUsers((rows) => rows.map((r) => ({ ...r, password: pwd })));
    toast.success(`Applied common password to all ${bulkUsers.length} row(s)`);
  };

  const submit = async () => {
    if (!bulkUsers.length) { toast.error("Add at least one member"); return; }
    const orgId = typeof user?.organizationId === "object" ? (user.organizationId?._id || user.organizationId?.id) : user?.organizationId;
    if (!orgId) { toast.error("Could not determine your organization"); return; }

    const valid = [];
    const errs = [];
    bulkUsers.forEach((r, i) => {
      if (!r.firstName || !r.lastName || !r.email) errs.push({ row: i + 1, email: r.email || "Unknown", error: "First Name, Last Name, and Email are required" });
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email)) errs.push({ row: i + 1, email: r.email, error: "Invalid email format" });
      else {
        const reportingManagersArray = resolveReportingManagerIdsForSubmit(r.reportingManagers, allUsers);
        const perRowPassword = r.password?.trim();
        valid.push({
          firstName: r.firstName.trim(),
          lastName: r.lastName.trim(),
          email: r.email.trim().toLowerCase(),
          role: r.role || "org_user",
          phone: r.phone?.trim() || "",
          department: r.department?.trim() || "",
          position: r.position?.trim() || "",
          reportingManager: reportingManagersArray[0] || "",
          reportingManagers: reportingManagersArray,
          organizationId: orgId,
          permissions: r.role === "org_user" ? r.permissions : undefined,
          billingEnabled: true,
          ...(perRowPassword ? { password: perRowPassword } : {}),
        });
      }
    });

    if (!valid.length) {
      const failures = mergeBulkImportErrors(errs, []);
      setImportResult({ successCount: 0, failures });
      return;
    }

    const duplicateErrors = collectDuplicateImportErrors(
      bulkUsers.filter((r) => r.firstName && r.lastName && r.email),
      allUsers,
    );
    if (duplicateErrors.length) {
      setImportResult({ successCount: 0, failures: duplicateErrors });
      toast.error(`${duplicateErrors.length} row(s) have duplicate or existing emails`);
      return;
    }

    setSubmitting(true);
    try {
      const updatedOrg = await api.get("/api/v1/billing/profile").then((res) => res.data.data).catch(() => null);
      if (updatedOrg && !updatedOrg.paymentBypassEnabled) {
        const isFreePlan = updatedOrg.orgPlanSelection === "free";
        const isPaidPlan = updatedOrg.orgPlanSelection === "standard" || updatedOrg.orgPlanSelection === "premium";
        const availableSlots = updatedOrg.remainingSlots || 0;
        if ((isPaidPlan || isFreePlan) && valid.length > availableSlots) {
          toast.error(`Only ${availableSlots} slot${availableSlots !== 1 ? "s" : ""} available, but you are trying to upload ${valid.length} user${valid.length !== 1 ? "s" : ""}.`, { duration: 5000 });
          setSubmitting(false);
          return;
        }
      }

      const res = await api.post("/api/v1/users/bulk", { users: valid });
      const { successCount = 0, errors: apiErrors = [] } = res.data?.data || {};
      const failures = mergeBulkImportErrors(errs, apiErrors);

      if (successCount > 0) {
        toast.success(`Successfully added ${successCount} user(s)!`);
        onDone?.();
      }

      if (failures.length > 0) {
        setImportResult({ successCount, failures });
        if (successCount === 0) {
          toast.error(`No members imported. ${failures.length} row(s) failed.`);
        } else {
          toast.error(`${failures.length} row(s) failed. See details below.`);
        }
      } else if (successCount > 0) {
        onClose();
      }
    } catch (err) {
      const data = err.response?.data;
      let msg = "Bulk import failed";
      if (Array.isArray(data?.error?.details) && data.error.details.length) msg = data.error.details.join(". ");
      else if (data?.error?.message) msg = data.error.message;
      else if (data?.message) msg = data.message;
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const paginated = bulkUsers.slice((page - 1) * pageSize, page * pageSize);
  const thStyle = React.useMemo(() => ({ textAlign: "left", padding: "10px 12px", fontSize: t.font.size.xs, fontWeight: 700, color: t.c.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap", borderBottom: `1px solid ${t.c.border}`, background: t.c.surfaceMuted }), [t]);
  const tdStyle = React.useMemo(() => ({ padding: "8px 12px", borderBottom: `1px solid ${t.c.border}`, verticalAlign: "middle" }), [t.c.border]);

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      title="Bulk import members"
      subtitle="Upload a CSV or Excel file"
      icon={<FileSpreadsheet size={18} />}
      width={1280}
      footer={
        <div style={{ display: "flex", gap: t.s[2], justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>{bulkUsers.length ? `${bulkUsers.length} member(s)` : ""}</span>
          <div style={{ display: "flex", gap: t.s[2] }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={submitting || !bulkUsers.some((r) => r.firstName && r.email)}>
              {submitting ? "Importing…" : `Import ${bulkUsers.length} member(s)`}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
        <div
          onClick={() => !isUploading && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
          style={{ cursor: isUploading ? "wait" : "pointer", border: `1.5px dashed ${t.c.border}`, borderRadius: t.r.md, padding: t.s[5], background: t.c.surfaceMuted }}
        >
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.s[3], flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: t.s[3] }}>
              <span style={{ display: "inline-flex", width: 40, height: 40, borderRadius: 10, alignItems: "center", justifyContent: "center", background: t.c.primarySoft, color: t.c.primary }}>
                <Upload size={20} />
              </span>
              <div>
                <div style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text }}>{fileName || "Upload CSV/Excel file"}</div>
                <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2 }}>Upload a CSV or Excel file to bulk import team members</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: t.s[2] }}>
              <Button variant="primary" size="sm" disabled={isUploading}>{isUploading ? "Uploading…" : "Choose file"}</Button>
              <Button variant="ghost" size="sm" leftIcon={<Download size={14} />} onClick={(e) => { e.stopPropagation(); downloadTemplate(); }}>Download template</Button>
            </div>
          </div>
        </div>

        {parseError && (
          <div style={{ display: "flex", gap: 8, fontSize: t.font.size.sm, color: t.status.negative, background: t.status.negativeSoft, padding: `${t.s[2]}px ${t.s[3]}px`, borderRadius: t.r.sm }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ whiteSpace: "pre-line", flex: 1 }}>{parseError}</div>
            <button type="button" onClick={() => setParseError("")} style={{ border: "none", background: "transparent", cursor: "pointer", color: t.status.negative }}><X size={14} /></button>
          </div>
        )}
        {uploadSuccess && (
          <div style={{ display: "flex", gap: 8, fontSize: t.font.size.sm, color: t.status.positive, background: t.status.positiveSoft, padding: `${t.s[2]}px ${t.s[3]}px`, borderRadius: t.r.sm }}>
            <CheckCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>{bulkUsers.length} user(s) imported. Review below, then click Import.</div>
            <button type="button" onClick={() => setUploadSuccess(false)} style={{ border: "none", background: "transparent", cursor: "pointer", color: t.status.positive }}><X size={14} /></button>
          </div>
        )}

        <Card padding={12} style={{ background: t.c.surfaceMuted }}>
          <div style={{ fontSize: t.font.size.xs, fontWeight: 600, color: t.c.text, marginBottom: 8 }}>Required columns</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {["First Name *", "Last Name *", "Email *", "Role", "Position", "Phone", "Reporting Manager", "Password"].map((col) => (
              <span key={col} style={{ fontSize: t.font.size.xs, padding: "3px 8px", borderRadius: 6, background: t.c.primarySoft, color: t.c.primary, fontWeight: 500 }}>{col}</span>
            ))}
          </div>
          <p style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, margin: "8px 0 0", lineHeight: 1.5 }}>
            Reporting Manager can be an email or name (comma-separated). If the email already exists in the team, it is applied automatically; otherwise it stays as entered for manual assignment later.
          </p>
        </Card>

        <Card padding={14} style={{ border: `1.5px dashed ${t.c.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: t.s[4] }}>
            <Zap size={16} style={{ color: t.c.primary }} />
            <div>
              <div style={{ fontSize: t.font.size.sm, fontWeight: 700, color: t.c.text }}>Apply to all rows</div>
              <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>Set common values for all members at once</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: t.s[3] }}>
            <Field label="Reporting managers">
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <BulkImportApplyMultiSelect
                    value={commonReportingManagers}
                    onChange={setCommonReportingManagers}
                    options={commonManagerOptions}
                    placeholder="Select managers"
                    dropdownPopup={dropdownPopup}
                  />
                </div>
                <Button variant="secondary" size="sm" onClick={applyReportingManagersToAll}>Apply</Button>
              </div>
            </Field>
            <Field label="Position">
              <div style={{ display: "flex", gap: 6 }}>
                {basePositionOptions.length === 0 ? (
                  <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={onAddPosition} style={{ flex: 1 }}>Add position</Button>
                ) : (
                  <>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <BulkImportApplyDropdown
                        value={commonPosition}
                        onChange={(v) => setCommonPosition(v || "")}
                        options={commonPositionOptions}
                        placeholder="Select position"
                        dropdownPopup={dropdownPopup}
                      />
                    </div>
                    <Button variant="secondary" size="sm" onClick={applyPositionToAll}>Apply</Button>
                  </>
                )}
              </div>
            </Field>
            <Field label="Role">
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <BulkImportApplyDropdown
                    value={commonRole}
                    onChange={setCommonRole}
                    options={roleDropdownOptions}
                    placeholder="Select role"
                    allowClear={false}
                    dropdownPopup={dropdownPopup}
                  />
                </div>
                <Button variant="secondary" size="sm" onClick={() => applyRoleToAll(commonRole)}>Apply</Button>
              </div>
            </Field>
            <Field label="Common password" hint="Optional — min 8 chars">
              <div style={{ display: "flex", gap: 6 }}>
                <Input type="text" value={commonPassword} onChange={(e) => setCommonPassword(e.target.value)} placeholder="One password for all" style={{ flex: 1 }} />
                <Button variant="secondary" size="sm" onClick={applyPasswordToAll}>Apply</Button>
              </div>
            </Field>
          </div>
          <div style={{ marginTop: t.s[3], paddingTop: t.s[3], borderTop: `1px solid ${t.c.border}` }}>
            <div style={{ fontSize: t.font.size.xs, fontWeight: 600, color: t.c.textSecondary, marginBottom: 8 }}>Common permissions (org_user rows)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              {[
                ["addProject", "Add project"],
                ["addTeamMember", "Add team member"],
                ["viewReports", "View reports"],
                ["viewActivity", "View activity"],
                ["editProfile", "Edit profile"],
                ...(showCrmPermission ? [["crmAccess", "CRM"]] : []),
              ].map(([key, label]) => (
                <label key={key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: t.font.size.xs, color: t.c.text, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!commonPermissions[key]} onChange={(e) => setCommonPermissions((p) => ({ ...p, [key]: e.target.checked }))} />
                  {label}
                </label>
              ))}
              <Button variant="secondary" size="sm" onClick={applyPermissionsToAll}>Apply permissions</Button>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: t.s[3], flexWrap: "wrap" }}>
              <Button variant="ghost" size="sm" onClick={() => { const d = prompt("Enter department to apply to all rows:"); if (d !== null) applyDepartmentToAll(d); }}>Apply department</Button>
              <Button variant="ghost" size="sm" onClick={() => { if (window.confirm("Clear all reporting managers from all rows?")) { setBulkUsers((rows) => rows.map((r) => ({ ...r, reportingManagers: [] }))); setCommonReportingManagers([]); toast.success("Cleared all reporting managers"); } }}>Clear all managers</Button>
            </div>
          </div>
        </Card>

        <div style={{ overflowX: "auto", border: `1px solid ${t.c.border}`, borderRadius: t.r.md }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1320, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 36 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 240 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 240 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 40 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={thStyle} />
                <th style={thStyle}>First name *</th>
                <th style={thStyle}>Last name *</th>
                <th style={thStyle}>Email *</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Position</th>
                <th style={thStyle}>Reporting manager</th>
                <th style={thStyle}>Department</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Password</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {paginated.map((row) => (
                <React.Fragment key={row.id}>
                  <tr>
                    <td style={tdStyle}>
                      <button type="button" onClick={() => toggleExpanded(row.id)} style={{ border: "none", background: "transparent", cursor: "pointer", color: t.c.textTertiary, transform: row.expanded ? "rotate(90deg)" : "none", transition: "transform 120ms" }}>
                        <ChevronRight size={16} />
                      </button>
                    </td>
                    <td style={tdStyle}><Input value={row.firstName} onChange={(e) => updateBulkUser(row.id, "firstName", e.target.value)} style={nameInputStyle} placeholder="First name" /></td>
                    <td style={tdStyle}><Input value={row.lastName} onChange={(e) => updateBulkUser(row.id, "lastName", e.target.value)} style={nameInputStyle} placeholder="Last name" /></td>
                    <td style={tdStyle}><Input type="email" value={row.email} onChange={(e) => updateBulkUser(row.id, "email", e.target.value)} style={emailInputStyle} placeholder="name@company.com" /></td>
                    <td style={tdStyle}>
                      <BulkImportRoleDropdown
                        value={row.role}
                        onChange={(v) => updateBulkUser(row.id, "role", v)}
                        options={roleDropdownOptions}
                        dropdownPopup={dropdownPopup}
                      />
                    </td>
                    <td style={tdStyle}>
                      {basePositionOptions.length > 0 ? (
                        <BulkImportPositionDropdown
                          value={row.position}
                          onChange={(v) => updateBulkUser(row.id, "position", v || "")}
                          position={row.position}
                          baseOptions={basePositionOptions}
                          dropdownPopup={dropdownPopup}
                        />
                      ) : (
                        <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={onAddPosition} style={{ width: "100%", justifyContent: "center" }}>
                          Add position
                        </Button>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <BulkImportRmInput
                        managers={row.reportingManagers}
                        allUsers={allUsers}
                        onCommit={(vals) => updateBulkUserReportingManagers(row.id, vals)}
                        style={rmInputStyle}
                      />
                    </td>
                    <td style={tdStyle}><Input value={row.department} onChange={(e) => updateBulkUser(row.id, "department", e.target.value)} style={textInputStyle} placeholder="Dept" /></td>
                    <td style={tdStyle}><Input type="tel" value={row.phone} onChange={(e) => updateBulkUser(row.id, "phone", e.target.value)} style={phoneInputStyle} placeholder="+91…" /></td>
                    <td style={tdStyle}><Input value={row.password} onChange={(e) => updateBulkUser(row.id, "password", e.target.value)} placeholder="Optional" style={textInputStyle} /></td>
                    <td style={tdStyle}>
                      <button type="button" onClick={() => removeRow(row.id)} disabled={bulkUsers.length <= 1} title="Remove row" style={{ border: "none", background: "transparent", cursor: bulkUsers.length <= 1 ? "not-allowed" : "pointer", color: t.c.textTertiary, opacity: bulkUsers.length <= 1 ? 0.4 : 1 }}><X size={16} /></button>
                    </td>
                  </tr>
                  {row.expanded && (
                    <tr>
                      <td colSpan={11} style={{ ...tdStyle, background: t.c.surfaceMuted }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: t.s[4], padding: t.s[2] }}>
                          {row.role === "org_user" && (
                            <div>
                              <div style={{ fontSize: t.font.size.xs, fontWeight: 600, color: t.c.textSecondary, marginBottom: 8 }}>Permissions</div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                {[
                                  ["addProject", "Add project"],
                                  ["addTeamMember", "Add team member"],
                                  ["viewReports", "View reports"],
                                  ["viewActivity", "View activity"],
                                  ["editProfile", "Edit profile"],
                                  ...(showCrmPermission ? [["crmAccess", "CRM"]] : []),
                                ].map(([key, label]) => (
                                  <label key={key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: t.font.size.xs, color: t.c.text, cursor: "pointer" }}>
                                    <input type="checkbox" checked={!!row.permissions?.[key]} onChange={(e) => updateBulkUserPermission(row.id, key, e.target.checked)} />
                                    {label}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {bulkUsers.length > pageSize && (
          <Pagination page={page} pages={Math.ceil(bulkUsers.length / pageSize)} total={bulkUsers.length} onChange={setPage} />
        )}

        <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addRow}>Add row</Button>
      </div>
    </Drawer>
    <BulkImportResultModal
      t={t}
      open={!!importResult}
      onClose={() => setImportResult(null)}
      successCount={importResult?.successCount || 0}
      failures={importResult?.failures || []}
    />
    </>
  );
}

export default BulkImportDrawer;
